import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { companiesAPI, jobsAPI } from '../../services/api';
import { formatSalary, timeAgo } from '../../utils/helpers';
import { FiMapPin, FiUsers, FiGlobe, FiMail, FiPhone, FiArrowLeft, FiBriefcase, FiStar, FiHeart } from 'react-icons/fi';
import toast from 'react-hot-toast';
import './Companies.css';

const CompanyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('about');

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await companiesAPI.get(id);
        setCompany(data.data || data.company || data);
      } catch (err) {
        toast.error('Company not found');
        navigate('/companies');
      }
      try {
        const { data } = await jobsAPI.list({ company_id: id });
        const d = data.data || data.jobs || data.results || (Array.isArray(data) ? data : []);
        setJobs(d);
      } catch (e) {}
      setLoading(false);
    };
    load();
  }, [id, navigate]);

  const handleFollow = async () => {
    try {
      await companiesAPI.follow(id);
      toast.success('Following company');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to follow');
    }
  };

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;
  if (!company) return null;

  return (
    <div className="company-detail-page">
      <div className="container">
        <Link to="/companies" className="back-link"><FiArrowLeft /> Back to Companies</Link>

        <div className="company-hero card">
          <div className="company-hero-inner">
            <div className="company-logo lg">{(company.name || 'C')[0]}</div>
            <div className="company-hero-info">
              <h1>{company.name}</h1>
              <div className="company-meta">
                {company.industry && <span>{company.industry}</span>}
                {(company.location || company.city) && <span><FiMapPin /> {company.location || company.city}</span>}
                {company.size && <span><FiUsers /> {company.size}</span>}
                {company.rating && <span><FiStar /> {company.rating}/5</span>}
              </div>
            </div>
            <button className="btn btn-secondary" onClick={handleFollow}><FiHeart /> Follow</button>
          </div>
        </div>

        <div className="tabs">
          <button className={`tab ${tab === 'about' ? 'active' : ''}`} onClick={() => setTab('about')}>About</button>
          <button className={`tab ${tab === 'jobs' ? 'active' : ''}`} onClick={() => setTab('jobs')}>Open Positions ({jobs.length})</button>
        </div>

        {tab === 'about' && (
          <div className="company-about-grid">
            <div className="card">
              <h3 style={{ marginBottom: '1rem' }}>About {company.name}</h3>
              <p style={{ color: 'var(--text-secondary)', lineHeight: 1.75 }}>
                {company.description || `${company.name} is a ${company.industry || ''} company based in South Africa. They are committed to finding and hiring talented professionals.`}
              </p>
            </div>
            <div className="card">
              <h3 style={{ marginBottom: '1rem' }}>Company Info</h3>
              <div className="company-info-list">
                {company.website && <div><FiGlobe /> <a href={company.website} target="_blank" rel="noopener noreferrer">{company.website}</a></div>}
                {company.email && <div><FiMail /> {company.email}</div>}
                {company.phone && <div><FiPhone /> {company.phone}</div>}
                {company.size && <div><FiUsers /> {company.size} employees</div>}
                {company.founded && <div>Founded {company.founded}</div>}
              </div>
            </div>
          </div>
        )}

        {tab === 'jobs' && (
          jobs.length > 0 ? (
            <div className="jobs-list-grid">
              {jobs.map((job) => (
                <Link to={`/jobs/${job.id}`} className="job-list-card" key={job.id}>
                  <div className="job-list-left">
                    <div>
                      <h3 style={{ fontSize: '1.05rem', marginBottom: '0.3rem' }}>{job.title}</h3>
                      <div className="job-list-meta">
                        <span><FiMapPin /> {job.location_name || job.location || 'SA'}</span>
                        <span>{job.job_type || 'Full-time'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="job-list-right">
                    <div className="job-salary">{formatSalary(job.salary_min, job.salary_max)}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{timeAgo(job.created_at)}</div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="empty-state"><div className="icon">💼</div><h3>No open positions</h3><p>Check back later for new opportunities</p></div>
          )
        )}
      </div>
    </div>
  );
};

export default CompanyDetail;
